import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import type { ApprovalAnswer, ChangeClass, Finding, GovernancePort, ImpactReport, Proposal } from './editor-types';

/**
 * Validation au serveur, analyse d'impact et circuit de publication (ADR-130 tranche 2, ADR-141 §2.7) :
 * brouillon → proposition → approbation par une seconde personne si le changement est structurel →
 * nouvelle version. Le socle n'autorise rien : il montre l'état et transmet les commandes ; le serveur
 * refuse (proposant qui s'approuve, session non élevée, base déplacée) et le refus s'affiche tel quel.
 */

const messageOf = (error: unknown): string => (error instanceof ApiError ? `${error.message} (${error.code})` : error instanceof Error ? error.message : String(error));

export function FindingList({ findings, onLocate, empty }: { findings: readonly Finding[]; onLocate: (path: string) => void; empty: string }) {
  if (findings.length === 0) return <p className="ay-text ay-text--muted">{empty}</p>;
  return (
    <ul className="ay-findings">
      {findings.map((finding, index) => (
        <li key={`${finding.path}-${finding.code}-${index}`} className={`ay-findings__item ay-findings__item--${finding.severity}`}>
          <button type="button" className="ay-findings__locate" onClick={() => onLocate(finding.path)}>
            <span className="ay-findings__severity">{finding.severity === 'error' ? 'Erreur' : 'Avertissement'}</span>
            <span className="ay-findings__message">{finding.message}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function ChangeBadge({ change, estimated, minor = 'Libellés seulement' }: { change: ChangeClass; estimated: boolean; minor?: string }) {
  if (change.changes.length === 0) return <span className="ay-badge">Aucune modification</span>;
  return (
    <span className={`ay-badge ${change.structural ? 'ay-badge--warning' : 'ay-badge--success'}`} title={estimated ? 'Estimation dans le navigateur ; le serveur décide à la proposition.' : undefined}>
      {change.structural ? 'Changement structurel : seconde personne' : minor}
      {estimated ? ' (estimation)' : ''}
    </span>
  );
}

export function GovernancePanel<D>({ port, draft, dirty, localErrors, estimate, onLocate, onPublished }: {
  port: GovernancePort<D>;
  draft: D;
  dirty: boolean;
  localErrors: number;
  estimate: ChangeClass;
  onLocate: (path: string) => void;
  onPublished: (proposal: Proposal) => void;
}) {
  const [validation, setValidation] = useState<{ valid: boolean; findings: Finding[] } | null>(null);
  const [impact, setImpact] = useState<ImpactReport | null>(null);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approval, setApproval] = useState<ApprovalAnswer | null>(null);

  useEffect(() => {
    let alive = true;
    port.pending().then((found) => alive && setProposal(found), (failure: unknown) => alive && setError(messageOf(failure)));
    return () => {
      alive = false;
    };
  }, [port]);
  // Un brouillon modifié invalide la validation et l'impact déjà calculés.
  useEffect(() => {
    setValidation(null);
    setImpact(null);
  }, [draft]);

  const run = async (label: string, action: () => Promise<void>): Promise<void> => {
    setBusy(label);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(null);
    }
  };

  const serverErrors = validation?.findings.filter((finding) => finding.severity === 'error').length ?? 0;
  const canPropose = proposal?.status !== 'proposed' && dirty && localErrors === 0 && validation !== null && validation.valid && reason.trim().length >= 3 && busy === null;
  // « Approuver » ne paraît que si le serveur dit oui (même règle qu'ApproveModelChange, politique du tenant) ;
  // la commande reste juge à l'exécution (ADR-146 T3).
  const pendingId = proposal !== null && proposal.status === 'proposed' ? proposal.id : null;
  useEffect(() => {
    setApproval(null);
    if (pendingId === null || proposal === null) return;
    let alive = true;
    port.canApprove(proposal).then((answer) => alive && setApproval(answer));
    return () => {
      alive = false;
    };
  }, [port, pendingId]);

  return (
    <div className="ay-governance">
      <section className="ay-governance__block" aria-labelledby="gov-validation">
        <h4 id="gov-validation" className="ay-props__subtitle">Validation par le serveur</h4>
        <p className="ay-text ay-text--muted">{port.wording?.validation ?? 'Contrat, expressions, règles de conformité citées, sélecteurs.'} Une erreur empêche de proposer ; un avertissement se lit et se décide.</p>
        <button type="button" className="ay-button ay-button--secondary" disabled={busy !== null} onClick={() => void run('validate', async () => setValidation(await port.validate(draft)))}>
          {busy === 'validate' ? 'Validation…' : 'Valider le brouillon'}
        </button>
        {validation !== null && (
          <>
            <p role="status" className={validation.valid ? 'ay-governance__ok' : 'ay-governance__ko'}>{validation.valid ? 'Le serveur accepte ce brouillon.' : `${serverErrors} erreur(s) à corriger.`}</p>
            <FindingList findings={validation.findings} onLocate={onLocate} empty="Aucun constat." />
          </>
        )}
      </section>

      <section className="ay-governance__block" aria-labelledby="gov-impact">
        <h4 id="gov-impact" className="ay-props__subtitle">Analyse d’impact</h4>
        <ChangeBadge change={impact?.change ?? estimate} estimated={impact?.change === undefined} {...(port.wording?.minor === undefined ? {} : { minor: port.wording.minor })} />
        <button type="button" className="ay-button ay-button--secondary" disabled={busy !== null} onClick={() => void run('impact', async () => setImpact(await port.impact(draft)))}>
          {busy === 'impact' ? 'Analyse…' : 'Analyser l’impact'}
        </button>
        {impact !== null && (
          <div className="ay-governance__impact">
            {impact.summary !== undefined && <p>{impact.summary}</p>}
            {impact.sections.map((section) => (
              <div key={section.title}>
                <h5 className="ay-governance__impact-title">{section.title}</h5>
                {section.items.length === 0 ? (
                  <p className="ay-text ay-text--muted">{section.empty}</p>
                ) : (
                  <ul className="ay-governance__impact-list">
                    {section.items.map((item) => (
                      <li key={`${item.label}-${item.detail ?? ''}`}>
                        {item.label}
                        {item.detail !== undefined && <span className="ay-text ay-text--muted"> — {item.detail}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="ay-governance__block" aria-labelledby="gov-publish">
        <h4 id="gov-publish" className="ay-props__subtitle">Publication</h4>
        {proposal !== null && proposal.status === 'proposed' ? (
          <>
            <p>
              Proposition <strong>{proposal.reference ?? proposal.id}</strong> en attente de décision ({proposal.structural ? 'structurelle' : (port.wording?.minor ?? 'libellés seulement').toLocaleLowerCase('fr')}). Motif : {proposal.reason}
            </p>
            {approval === null && <p className="ay-text ay-text--muted" role="status">Vérification de votre droit d’approuver…</p>}
            {approval !== null && !approval.can && <p className="ay-governance__ko">{approval.message ?? 'Vous ne pouvez pas approuver cette proposition.'}</p>}
            <label className="ay-field">
              <span className="ay-field__label">Note de décision</span>
              <textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
            </label>
            <div className="ay-stack">
              {approval?.can === true && (
                <button type="button" className="ay-button" disabled={busy !== null} onClick={() => void run('approve', async () => {
                  const decided = await port.approve(proposal, note.trim() === '' ? undefined : note.trim());
                  setProposal(decided);
                  if (decided.status === 'published') onPublished(decided);
                })}>
                  Approuver et publier
                </button>
              )}
              <button type="button" className="ay-button ay-button--secondary" disabled={busy !== null || note.trim().length < 3} onClick={() => void run('reject', async () => setProposal(await port.reject(proposal, note.trim())))}>
                Refuser (motif dans la note)
              </button>
            </div>
          </>
        ) : (
          <>
            {proposal !== null && (
              <p className="ay-text ay-text--muted">
                Dernière proposition {proposal.reference ?? proposal.id} : {proposal.status === 'published' ? `publiée${proposal.publishedVersion === undefined ? '' : ` en ${proposal.publishedVersion}`}` : `refusée${proposal.decisionNote === undefined ? '' : ` (${proposal.decisionNote})`}`}.
              </p>
            )}
            <label className="ay-field">
              <span className="ay-field__label">Motif du changement *</span>
              <textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
            </label>
            <ul className="ay-governance__checks">
              <li className={dirty ? 'is-ok' : ''}>Le brouillon diffère de la version active</li>
              <li className={localErrors === 0 ? 'is-ok' : ''}>Aucune erreur locale</li>
              <li className={validation?.valid === true ? 'is-ok' : ''}>Brouillon validé par le serveur</li>
              <li className={reason.trim().length >= 3 ? 'is-ok' : ''}>Motif saisi</li>
            </ul>
            <button type="button" className="ay-button" disabled={!canPropose} onClick={() => void run('propose', async () => setProposal(await port.propose(draft, reason.trim())))}>
              {busy === 'propose' ? 'Proposition…' : 'Proposer la publication'}
            </button>
          </>
        )}
        {error !== null && <p role="alert" className="ay-field__error">{error}</p>}
      </section>
    </div>
  );
}
